import { useEffect, useState } from 'react';
import {
  dashboardService,
  type DashboardStats,
} from '@/features/dashboard/services/dashboard-service';
import type { ExchangeSummaryResponse } from '@/features/dashboard/types/exchange-rate.types';
import { dynamicPricingService } from '@/features/dynamic-pricing/services/dynamic-pricing-service';
import type {
  PricePositionResponse,
  ResumenPrecios,
} from '@/features/dynamic-pricing/types/dynamic-pricing.types';
import { tradeService } from '@/features/trade/services/trade-service';
import type { TariffStatus } from '@/features/trade/types/trade.types';
import { comerciaService } from '@/features/comercia/services/comercia-service';
import type { ImportacionDeComercia } from '@/features/comercia/types/comercia.types';

export interface ResumenDeInicio {
  cotiza: { cargando: boolean; stats: DashboardStats | null };
  compite: {
    cargando: boolean;
    posicion: PricePositionResponse | null;
    resumen: ResumenPrecios | null;
  };
  comercia: {
    cargando: boolean;
    /** La importación que se está armando en Comercia (28-oct: Comercia en tres pasos). */
    importacion: ImportacionDeComercia | null;
    tarifas: TariffStatus | null;
    cambio: ExchangeSummaryResponse | null;
  };
}

/** El valor de una promesa que pudo fallar: null si falló (cada fuente cae sola). */
const valor = <T>(r: PromiseSettledResult<T>): T | null =>
  r.status === 'fulfilled' ? r.value : null;

/**
 * Los números de las tres tarjetas de Inicio. Sólo se piden los de los módulos
 * que la persona tiene (los demás ni se piden: darían 403), y cada fuente falla
 * sola: si una no responde, su tarjeta muestra «—» y las otras siguen.
 */
export function useResumenDeInicio(modulos: {
  cotiza: boolean;
  compite: boolean;
  comercia: boolean;
}): ResumenDeInicio {
  const [r, setR] = useState<ResumenDeInicio>({
    cotiza: { cargando: modulos.cotiza, stats: null },
    compite: { cargando: modulos.compite, posicion: null, resumen: null },
    comercia: { cargando: modulos.comercia, importacion: null, tarifas: null, cambio: null },
  });

  useEffect(() => {
    let vivo = true;
    if (modulos.cotiza) {
      void Promise.allSettled([dashboardService.getStats()]).then(([stats]) => {
        if (vivo) setR((prev) => ({ ...prev, cotiza: { cargando: false, stats: valor(stats) } }));
      });
    }
    if (modulos.compite) {
      void Promise.allSettled([
        dynamicPricingService.getPricePosition(),
        dynamicPricingService.getResumen(),
      ]).then(([posicion, resumen]) => {
        if (vivo)
          setR((prev) => ({
            ...prev,
            compite: { cargando: false, posicion: valor(posicion), resumen: valor(resumen) },
          }));
      });
    }
    if (modulos.comercia) {
      void Promise.allSettled([
        comerciaService.actual(),
        tradeService.getTariffStatus(),
        dashboardService.getExchangeSummary(),
      ]).then(([actual, tarifas, cambio]) => {
        if (vivo)
          setR((prev) => ({
            ...prev,
            comercia: {
              cargando: false,
              importacion: valor(actual)?.importacion ?? null,
              tarifas: valor(tarifas),
              cambio: valor(cambio),
            },
          }));
      });
    }
    return () => {
      vivo = false;
    };
  }, [modulos.cotiza, modulos.compite, modulos.comercia]);

  return r;
}
